import { UniformBuffer } from '../UniformBuffer';
import { unrefTexture, refTexture } from '../Texture';
import { initTexture, createSamplerDesc } from "../Texture.js";
export var UniformType;
(function (UniformType) {
    // Scalar types
    UniformType["f32"] = "f32";
    UniformType["i32"] = "i32";
    // Vector types (2, 3, and 4 components)
    UniformType["vec2f"] = "vec2f";
    UniformType["vec3f"] = "vec3f";
    UniformType["vec4f"] = "vec4f";
    // Matrix types (2x2, 3x3, and 4x4)
    UniformType["mat3x3f"] = "mat3x3f";
    UniformType["mat4x4f"] = "mat4x4f";
    // THREE types
    UniformType["c"] = "c";
    // Todo:
    //  Currently, we only support what UniformBuffer supports directly via setters.
    //  This list is not complete. Add more types as needed (https://www.w3.org/TR/WGSL/) or find a less manual solution here.
})(UniformType || (UniformType = {}));
var ThreeUniformType;
(function (ThreeUniformType) {
    ThreeUniformType["i"] = "i";
    ThreeUniformType["f"] = "f";
    ThreeUniformType["v2"] = "v2";
    ThreeUniformType["v3"] = "v3";
    ThreeUniformType["v4"] = "v4";
    ThreeUniformType["c"] = "c";
    ThreeUniformType["m3"] = "m3";
    ThreeUniformType["m4"] = "m4";
})(ThreeUniformType || (ThreeUniformType = {}));
function getWebGPUUniformType(threeMaterialUniformType) {
    switch (threeMaterialUniformType) {
        case ThreeUniformType.i: return UniformType.i32;
        case ThreeUniformType.f: return UniformType.f32;
        case ThreeUniformType.v2: return UniformType.vec2f;
        case ThreeUniformType.v3: return UniformType.vec3f;
        case ThreeUniformType.v4: return UniformType.vec4f;
        case ThreeUniformType.c: return UniformType.c;
        case ThreeUniformType.m3: return UniformType.mat3x3f;
        case ThreeUniformType.m4: return UniformType.mat4x4f;
        default: return null;
    }
}
function getUniformSizeInFloats(type) {
    switch (type) {
        case UniformType.f32:
        case UniformType.i32:
            return 1;
        case UniformType.vec2f:
            return 2;
        case UniformType.vec3f:
        case UniformType.c:
            return 3;
        case UniformType.vec4f:
            return 4;
        case UniformType.mat3x3f:
            return 12; // consider extra floats for padding (see UniformBuffer.setMatrix3x3)
        case UniformType.mat4x4f:
            return 16;
        default:
            throw new Error(`Unknown uniform type: ${type}`);
    }
}
function getAlignmentInFloats(type) {
    switch (type) {
        case UniformType.f32:
        case UniformType.i32:
            return 1;
        case UniformType.vec2f:
            return 2;
        case UniformType.vec3f:
        case UniformType.c:
            return 4;
        case UniformType.vec4f:
            return 4;
        case UniformType.mat3x3f:
            return 4;
        case UniformType.mat4x4f:
            return 4;
        default:
            throw new Error(`Unknown uniform type: ${type}`);
    }
}
function setUniformBufferValue(buffer, offset, type, value) {
    switch (type) {
        case UniformType.f32:
            buffer.setFloat(offset, value);
            break;
        case UniformType.i32:
            buffer.setInt(offset, value);
            break;
        case UniformType.vec2f:
            buffer.setFloat2(offset, value.x, value.y);
            break;
        case UniformType.vec3f:
            buffer.setVector3(offset, value);
            break;
        case UniformType.c:
            buffer.setColor(offset, value);
            break;
        case UniformType.vec4f:
            buffer.setVector4(offset, value);
            break;
        case UniformType.mat3x3f:
            buffer.setMatrix3x3(offset, value);
            break;
        case UniformType.mat4x4f:
            buffer.setMatrix4x4(offset, value);
            break;
        default:
            throw new Error(`Unknown uniform type: ${type}`);
    }
}
// Computes required size of a UniformBuffer
function getRequiredBufferSize(uniforms, offsets // filled if provided
) {
    let offset = 0;
    for (const name in uniforms) {
        const type = uniforms[name];
        const align = getAlignmentInFloats(type);
        const remainder = offset % align;
        if (remainder != 0) {
            offset += align - remainder;
        }
        if (offsets) {
            offsets[name] = offset;
        }
        offset += getUniformSizeInFloats(type);
    }
    return offset;
}
/**
 * Extracts the uniform buffer layout and values from a THREE.ShaderMaterial
 * in the format expected by UniformGroup.
 * @param {THREE.ShaderMaterial} material - The material to extract the uniform buffer info from.
 */
export function extractUniformBufferInfo(material) {
    const uniformBufferLayout = {};
    const uniformBufferValues = {};
    for (const key in material.uniforms) {
        const uniform = material.uniforms[key];
        const threeMaterialUniformType = uniform.type;
        const webGPUUniformType = getWebGPUUniformType(threeMaterialUniformType);
        if (webGPUUniformType) {
            uniformBufferLayout[key] = webGPUUniformType;
            uniformBufferValues[key] = uniform.value;
        }
    }
    return { uniformBufferLayout, uniformBufferValues };
}
// Given a list of simple uniforms, this function creates
//   - A uniform buffer
//   - An offset table to match names with offsets
function createUniformBuffer(uniforms, visibility) {
    const offsets = {};
    const sizeInFloats = getRequiredBufferSize(uniforms, offsets);
    const visibleInFragment = Boolean(visibility & GPUShaderStage.FRAGMENT);
    const visibleInVertex = Boolean(visibility & GPUShaderStage.VERTEX);
    const buffer = new UniformBuffer(undefined, sizeInFloats, visibleInFragment, visibleInVertex);
    return { buffer, offsets };
}
export function getDefaultSampler() {
    // Note that it would also work to just return {} here, but we make it explicit for clarity.
    return {
        addressModeU: "clamp-to-edge",
        addressModeV: "clamp-to-edge",
        addressModeW: "clamp-to-edge",
        magFilter: "linear",
        minFilter: "linear",
        mipmapFilter: "linear",
        lodMinClamp: 0.0,
        lodMaxClamp: 10.0,
        maxAnisotropy: 16
    };
}
// Convenience class to control layout and values of a single WebGPU bindGroup.
export class UniformGroup {
    #label;
    // All maps indexed by binding number
    #uniformBufferInfos = new Map();
    #textureInfos = new Map();
    #samplerInfos = new Map();
    // Created from descriptions above
    #bindGroupLayoutDesc;
    #bindGroupLayout;
    #bindGroup;
    #bindGroupEntries; // array and items are reused unless layout changes
    #defaultTexture;
    #defaultTextureView;
    // Trigger check for outdated uniform buffers on next update
    #uniformBufferChanged = false;
    #shouldCreateMissingSamplers = false;
    constructor(label) {
        this.#label = label;
    }
    /**
     * Creates a UniformGroup from a THREE.ShaderMaterial.
     * @param {GPUDevice} device - The GPUDevice to create the UniformGroup with.
     * @param {Renderer} renderer - The renderer to get the placeholder texture from.
     * @param {THREE.ShaderMaterial} material - The material to create the UniformGroup from.
     * @param {MipmapPipeline} mipmapPipeline - The mipmap pipeline to use for texture initialization.
     * @returns {UniformGroup} The created UniformGroup.
     */
    static createFromCustomMaterial(device, renderer, material, mipmapPipeline) {
        const customMaterialUniforms = new UniformGroup(material.webGPU.labelPrefix);
        customMaterialUniforms.setDefaultTexture(renderer.getPlaceholderTexture().texture);
        const { uniformBufferLayout, uniformBufferValues } = extractUniformBufferInfo(material);
        customMaterialUniforms.addUniformBuffer(0, uniformBufferLayout, material.webGPU.uniformBufferVisibility, material.webGPU.uniformBufferBindingLayout);
        customMaterialUniforms.setUniformBufferValues(uniformBufferValues);
        for (let textureBinding of material.webGPU.textureBindings) {
            if (textureBinding.uniformKey && textureBinding.binding) {
                const textureUniform = material.uniforms[textureBinding.uniformKey];
                if (textureUniform.type !== 't') {
                    continue;
                }
                if (textureUniform.value === null) {
                    textureUniform.value = renderer.getPlaceholderTexture().texture;
                }
                // GPUTextures can be used as is, while THREE.Textures need to be initialized.
                const isGPUTexture = textureUniform.value instanceof GPUTexture;
                let texture;
                if (isGPUTexture) {
                    texture = textureUniform.value;
                }
                else {
                    initTexture(device, mipmapPipeline, textureUniform.value);
                    texture = textureUniform.value.__gpuTexture;
                }
                customMaterialUniforms.addTexture(textureBinding.binding, texture, textureBinding.textureVisibility, textureBinding.sampleType);
                if (textureBinding.samplerBinding) {
                    if (isGPUTexture) {
                        console.warn("'samplerBinding' can only be used for a THREE.Texture. For a GPUTexture define 'samplers' instead.");
                    }
                    else {
                        const samplerDesc = createSamplerDesc(textureUniform.value);
                        customMaterialUniforms.addSampler(textureBinding.samplerBinding, textureBinding.samplerVisibility, samplerDesc);
                    }
                }
            }
        }
        if (material.webGPU.samplers) {
            for (const sampler of material.webGPU.samplers) {
                customMaterialUniforms.addSampler(sampler.binding, sampler.visibility, sampler.descriptor);
            }
        }
        return customMaterialUniforms;
    }
    // WebGPU doesn't tolerate null textures. If a default texture is set, it will be used as a fallback whenever texture uniforms are null.
    setDefaultTexture(tex) {
        const texView = tex?.createView();
        if (tex == this.#defaultTexture) {
            return;
        }
        // update affected bindGroup entries if any
        this.#textureInfos.forEach(info => {
            if (!info.texture && info.bindGroupEntry?.resource) {
                // Replace the texture. This requirs a rebuild of the bindGroup
                info.bindGroupEntry.resource = texView;
                this.#bindGroup = null;
            }
        });
        unrefTexture(this.#defaultTexture);
        refTexture(tex);
        this.#defaultTexture = tex || null;
        this.#defaultTextureView = texView || null;
    }
    /**
     * Used to check if a default texture is set. As the default texture is usually set
     * when the UniformGroup is first set up, this also serves as an "isInitialized" check.
     */
    isDefaultTextureSet() {
        return !!this.#defaultTexture;
    }
    //
    // The add-functions below are used to declare all variables in the bindGroup. This includes...
    //  - Binding:              Assigning Uniforms, Samplers and Textures to binding numbers
    //  - UniformBufferLayouts: For each UniformBuffer, it defines name and type for each variable
    //  - Visibility:           Which shader stages can access the binding
    //
    // You can set or change values for the variables later, but must ensure that all values are specified before rendering.
    addUniformBuffer(binding, // the binding index of the buffer in the shader
    uniformBufferLayout, // plain object to specify member names and types within UniformBuffer
    visibility = GPUShaderStage.FRAGMENT | GPUShaderStage.VERTEX, bufferBindingLayout = {}) {
        if (this.#bindingAlreadyUsed(binding)) {
            return;
        }
        // Prepare layout entry for WebGPU
        const entry = {
            binding,
            visibility,
            buffer: bufferBindingLayout
        };
        // Combined stuff for managing the UniformBuffer
        const info = {
            uniformBufferLayout,
            uniformBufferValues: {},
            layoutEntry: entry,
            needsUpload: true,
            // auto-generated later
            bindGroupEntry: null,
            uniformBuffer: null,
            offsets: null
        };
        this.#uniformBufferInfos.set(binding, info);
        this.#onLayoutChanged();
    }
    addTexture(binding, texture, visibility = GPUShaderStage.FRAGMENT, sampleType = 'float') {
        if (this.#bindingAlreadyUsed(binding)) {
            return;
        }
        // Prepare layout entry for WebGPU
        const entry = {
            binding,
            visibility,
            texture: {
                sampleType,
            },
        };
        // Combined stuff for managing this texture
        const info = {
            texture,
            textureView: texture?.createView() || null,
            layoutEntry: entry,
            bindGroupEntry: null // assigned on next update
        };
        this.#textureInfos.set(binding, info);
        this.#onLayoutChanged();
    }
    addSampler(binding, visibility = GPUShaderStage.FRAGMENT, samplerDesc = getDefaultSampler(), samplerLayoutDesc = {} // defaults in WebGPU to { type: 'filtering' }
    ) {
        if (this.#bindingAlreadyUsed(binding)) {
            return;
        }
        // Prepare layout entry for WebGPU
        const entry = {
            binding,
            visibility,
            sampler: samplerLayoutDesc,
        };
        // Combined stuff for managing this sampler
        const info = {
            samplerDesc: samplerDesc,
            layoutEntry: entry,
            // generated on next update:
            sampler: null,
            bindGroupEntry: null
        };
        this.#samplerInfos.set(binding, info);
        this.#onLayoutChanged();
    }
    //
    // The set-functions below require that variables were already added using the fuction above.
    //
    setUniformBufferValues(values, binding = 0) {
        // Find UniformBuffer for this binding
        const info = this.#uniformBufferInfos.get(binding);
        if (!info) {
            console.error('Uniform buffer not found for binding: ', binding);
            return;
        }
        // Overload all specified values
        for (const prop in values) {
            const type = info.uniformBufferLayout[prop];
            if (!type) {
                console.error('Uniform name not found in the UniformBufferLayout: ', prop);
                continue;
            }
            info.uniformBufferValues[prop] = values[prop];
            // If uniformBuffer exists already, pass values directly.
            // Otherwise, they will be set as soon as the UniformBuffer is created.
            if (info.uniformBuffer) {
                setUniformBufferValue(info.uniformBuffer, info.offsets[prop], type, values[prop]);
            }
        }
        // TODO: Allow auto-check to avoid unnecessary uploading if the values are the same as before
        info.needsUpload = true;
        this.#uniformBufferChanged = true;
    }
    setTexture(texture, binding) {
        const info = this.#textureInfos.get(binding);
        if (!info) {
            console.error('Texture not found for binding: ', binding);
            return;
        }
        unrefTexture(info.texture);
        refTexture(texture);
        info.texture = texture;
        info.textureView = texture?.createView() || this.#defaultTextureView;
        // Update bindGroup entry (only exists if there was an update call before)
        if (info.bindGroupEntry) {
            info.bindGroupEntry.resource = info.textureView;
        }
        // If bindGroup is already created, we need to update it
        this.#bindGroup = null;
    }
    setSampler(samplerDesc, binding) {
        const info = this.#samplerInfos.get(binding);
        if (!info) {
            console.error('Sampler not found for binding: ', binding);
            return;
        }
        // Update the sampler descriptor
        info.samplerDesc = samplerDesc;
        // Force recreation of the sampler on next update
        // Sampler will be recreated in #createMissingSamplers during next update()
        info.sampler = null;
        this.#shouldCreateMissingSamplers = true;
        // If bindGroup is already created, we need to recreate it
        this.#bindGroup = null;
        // We also need to recreate the bindGroupEntries to make sure the sampler is assigned
        this.#bindGroupEntries = null;
    }
    // Returns true if the binding index is assigned (to UniformBuffer, Sampler, or Texture)
    isAssigned(binding) {
        return this.#getBindingInfo(binding) !== undefined;
    }
    //
    // Used for rendering
    //
    // Must be called before rendering. Makes sure that all resources are ready to render.
    update(device) {
        // We always reset #bindGroupLayout to null on changes - so existing means up-to-date
        if (!this.#bindGroupLayout) {
            this.#createBindGroupLayout(device);
            // Make sure that all required UniformBuffers and samplers exist.
            // Currently, we only need this if the layout changed.
            this.#createMissingUniformBuffers(device);
            this.#shouldCreateMissingSamplers = true;
        }
        if (this.#shouldCreateMissingSamplers) {
            this.#createMissingSamplers(device);
            this.#shouldCreateMissingSamplers = false;
        }
        this.#updateBindGroup(device);
        this.#updateUniformBufferValues();
    }
    /**
     * Updates bound GPUTextures (and their views) from the material's uniforms.
     * Has no effect on bound THREE.Textures.
     *
     * The main use case is to support canvas-related render targets that need to be updated on resize.
     * E.g. SheetRenderContext uses a custom shader that binds the renderer's color and id target.
     *
     * Custom material uniforms must have been initialized before calling this.
     * This invalidates the custom material bind group. Update the uniform group to regenerate it before rendering.
     *
     * @param {THREE.ShaderMaterial} material - The material to update the GPUTextures from.
     */
    updateGPUTextures(material) {
        for (let textureBinding of material.webGPU.textureBindings) {
            if (textureBinding.uniformKey && textureBinding.binding) {
                const textureUniform = material.uniforms[textureBinding.uniformKey];
                if (textureUniform.type !== 't' || textureUniform.value === null) {
                    continue;
                }
                const isGPUTexture = textureUniform.value instanceof GPUTexture;
                if (isGPUTexture) {
                    // Replace the existing texture. This invalidates the materials bind group.
                    // This should be cheap for GPUTextures. For THREE.Textures this might cause reallocation.
                    this.setTexture(textureUniform.value, textureBinding.binding);
                }
            }
        }
    }
    updateFromMaterial(device, material) {
        this.updateGPUTextures(material);
        const { uniformBufferValues } = extractUniformBufferInfo(material);
        this.setUniformBufferValues(uniformBufferValues);
        this.update(device);
    }
    // May be null if layout changed since last update() call
    get layout() {
        return this.#bindGroupLayout;
    }
    // May be null if bindGroup changed since last update() call
    get bindGroup() {
        return this.#bindGroup;
    }
    //
    // The private stuff...
    //
    // Make sure that all uniform buffers exists
    #createMissingUniformBuffers(device) {
        for (const info of this.#uniformBufferInfos.values()) {
            const needsUpdate = (!info.uniformBuffer || !info.offsets);
            if (needsUpdate) {
                this.#createUniformBuffer(device, info);
                this.#updateUniformBuffer(info);
            }
        }
    }
    #createUniformBuffer(device, info) {
        const { buffer, offsets } = createUniformBuffer(info.uniformBufferLayout, info.layoutEntry.visibility);
        buffer.setDevice(device);
        info.uniformBuffer = buffer;
        info.offsets = offsets;
    }
    // Performs a full update of a UniformBuffer
    #updateUniformBuffer(info) {
        for (const prop in info.uniformBufferValues) {
            const type = info.uniformBufferLayout[prop];
            setUniformBufferValue(info.uniformBuffer, info.offsets[prop], type, info.uniformBufferValues[prop]);
            info.uniformBuffer.upload();
            info.needsUpload = false;
        }
    }
    #createMissingSamplers(device) {
        this.#samplerInfos.forEach(info => {
            if (!info.sampler) {
                info.sampler = device.createSampler(info.samplerDesc);
            }
        });
    }
    #createBindGroupLayout(device) {
        const entries = [];
        // collect layout entries for uniformBuffers
        for (const info of this.#uniformBufferInfos.values()) {
            entries.push(info.layoutEntry);
        }
        // same for textures
        for (const info of this.#textureInfos.values()) {
            entries.push(info.layoutEntry);
        }
        // same for samplers
        for (const info of this.#samplerInfos.values()) {
            entries.push(info.layoutEntry);
        }
        this.#bindGroupLayoutDesc = { label: this.#label, entries };
        this.#bindGroupLayout = device.createBindGroupLayout(this.#bindGroupLayoutDesc);
    }
    // Requires that uniforms and samplers are already created.
    #updateBindGroup(device) {
        // Note: The bindGroupEntries array and items can be resused as long as the layout
        //       doesn't change. On value changes, we only change the values in-place.
        if (!this.#bindGroupEntries) {
            this.#createBindGroupEntries();
        }
        // We reset the bindGroup on changes, so an existence check is enough here
        if (!this.#bindGroup) {
            this.#bindGroup = device.createBindGroup({
                label: this.#label,
                layout: this.#bindGroupLayout,
                entries: this.#bindGroupEntries
            });
        }
    }
    #getGPUBindingResource(info) {
        const buffer = info.uniformBuffer?.getBuffer();
        const texture = info.textureView;
        const sampler = info.sampler;
        if (buffer)
            return { buffer };
        if (texture)
            return texture;
        if (sampler)
            return sampler;
        return null;
    }
    // Requires layout and bindGroupEntries array to exist
    #addBindGroupEntry(info) {
        const entry = {
            // Layout must be update already, so all layoutEntries exist
            binding: info.layoutEntry.binding,
            resource: this.#getGPUBindingResource(info) // WebGPU types insist on non-null, but we will ensure this before rendering
        };
        this.#bindGroupEntries.push(entry);
        // link entry to info for later updates
        info.bindGroupEntry = entry;
    }
    #createBindGroupEntries() {
        this.#bindGroupEntries = [];
        // add bindings for uniform buffers
        for (const info of this.#uniformBufferInfos.values()) {
            this.#addBindGroupEntry(info);
        }
        // add bindings for texture
        for (const info of this.#textureInfos.values()) {
            // warn if texture is missing
            if (!info.textureView && !this.#defaultTextureView) {
                console.error(`Texture is missing for binding: ${info.layoutEntry.binding}. Null textures are only allowed if a default texture is provided`);
                // We could continue here, but we will eventually hit WebGPU errors anyway. So it's better if the WebGPU errors hit the point.
            }
            this.#addBindGroupEntry(info);
        }
        // add bindings for samplers
        for (const info of this.#samplerInfos.values()) {
            this.#addBindGroupEntry(info);
        }
    }
    // Makes sure that all resources are re-created on next update call
    #invalidateAll() {
        // Remove all auto-generated stuff
        this.#bindGroupLayoutDesc = null;
        this.#bindGroupLayout = null;
        this.#bindGroup = null;
        this.#uniformBufferInfos.forEach(info => {
            info.bindGroupEntry = null;
            info.uniformBuffer = null;
            info.offsets = null;
        });
        this.#textureInfos.forEach(info => {
            info.bindGroupEntry = null;
        });
        this.#samplerInfos.forEach(info => {
            info.bindGroupEntry = null;
        });
    }
    #onLayoutChanged() {
        // Layout isn't expected to change often. So we just invalidate all
        // unless we find a reason to optimize.
        this.#invalidateAll();
    }
    #getBindingInfo(binding) {
        const tex = this.#uniformBufferInfos.get(binding);
        const ub = this.#textureInfos.get(binding);
        const sampler = this.#samplerInfos.get(binding);
        return tex || ub || sampler;
    }
    // Returns true and logs an error if a binding is already assigned, false otherwise.
    #bindingAlreadyUsed(binding) {
        if (this.#getBindingInfo(binding)) {
            console.error('Binding ', binding, ' is assigned.');
            return true;
        }
        return false;
    }
    // info.uniform buffer must exist already
    #updateUniformBufferValues() {
        if (!this.#uniformBufferChanged) {
            return;
        }
        for (const info of this.#uniformBufferInfos.values()) {
            if (info.needsUpload) {
                info.uniformBuffer.upload();
            }
        }
        this.#uniformBufferChanged = false;
    }
}
